import { useState } from 'react';
import { billingApi, portalApi, podsApi, type PortalDocumentIndexRow, type PortalInvoice } from '@/api/client';
import { useAsync } from '@/hooks';
import { useAuth } from '@/auth/AuthContext';
import { useToast } from '@/components/Feedback';
import { PortalLayout } from '@/components/Layout';
import { PortalNav } from '@/components/Nav';
import { StatusBadge } from '@/components/StatusBadge';
import { Link } from 'react-router-dom';
import { Button } from '@/components/Button';
import { formatDate, formatPeso, stopTypeLabel } from '@/lib/format';

export function PortalDocuments() {
  const { data: rows, loading, error, reload } = useAsync(() => portalApi.documentIndex(), []);
  const { hasPermission } = useAuth();
  // Invoices carry amounts; Client Viewer (POD-download-only) doesn't see them.
  const canSeeInvoices = hasPermission('portal.view_trips');
  const invoicesState = useAsync(
    () => (canSeeInvoices ? portalApi.invoices() : Promise.resolve([] as PortalInvoice[])),
    [canSeeInvoices],
  );
  const toast = useToast();
  const [downloading, setDownloading] = useState<string | null>(null);

  const download = async (id: string, run: () => Promise<void>) => {
    setDownloading(id);
    try {
      await run();
    } catch (err) {
      toast((err as { message?: string }).message ?? 'The PDF could not be downloaded. Please try again.', 'error');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <PortalLayout>
      <div className="page">
        <div className="portal-nav">
          <PortalNav variant="top" />
        </div>

        <div className="page-header row-between">
          <div>
            <h1>Documents</h1>
            <p className="text-muted">
              Proof of delivery for each of your trips, and your invoices. Open a trip to see its full
              details.
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              reload();
              invoicesState.reload();
            }}
          >
            Refresh
          </Button>
        </div>

        {canSeeInvoices && (
        <section className="doc-section">
          <h2>Invoices</h2>
          {invoicesState.loading && <p className="text-muted">Loading invoices…</p>}
          {!invoicesState.loading && invoicesState.error && (
            <p className="text-muted">
              Invoices could not be loaded.{' '}
              <Button variant="secondary" size="sm" type="button" onClick={() => invoicesState.reload()}>
                Retry
              </Button>
            </p>
          )}
          {!invoicesState.loading && !invoicesState.error && !invoicesState.data?.length && (
            <p className="text-muted">Your invoices will appear here once they are issued.</p>
          )}
          {!!invoicesState.data?.length && (
            <ul className="invoice-list">
              {invoicesState.data.map((inv: PortalInvoice) => (
                <li key={inv.id} className="doc-card invoice-row">
                  <div>
                    <strong>{inv.invoiceNumber}</strong>
                    <div className="text-muted" style={{ fontSize: '0.8125rem' }}>
                      {formatDate(inv.periodStart)} to {formatDate(inv.periodEnd)} · Issued {formatDate(inv.issuedAt)}
                      {inv.paid ? ' · Paid' : ''}
                    </div>
                  </div>
                  <div className="invoice-actions">
                    <strong>{formatPeso(inv.total)}</strong>
                    <Button
                      size="sm"
                      variant="secondary"
                      type="button"
                      loading={downloading === inv.id}
                      onClick={() =>
                        void download(inv.id, () => billingApi.downloadInvoicePdf(inv.id, inv.invoiceNumber))
                      }
                    >
                      Download PDF
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
        )}

        <h2 className="doc-section-title">Proof of delivery</h2>

        {error && (
          <div className="form-error" style={{ marginBottom: 'var(--space-md)' }}>
            {error.includes('organisation')
              ? "Your login isn't linked to a company account yet. Please contact your account manager."
              : error}
          </div>
        )}

        {loading && <p className="text-muted">Loading documents…</p>}

        {!loading && !rows?.length && (
          error ? (
            <p className="text-muted">
              Documents could not be loaded.{' '}
              <Button variant="secondary" size="sm" type="button" onClick={() => reload()}>Retry</Button>
            </p>
          ) : (
            <p className="text-muted">Proof of delivery will appear here after each delivery is completed.</p>
          )
        )}

        <div className="doc-cards">
          {(rows ?? []).map((row: PortalDocumentIndexRow) => {
            const podCount = row.documents.filter((d) => d.podId).length;
            return (
              <article key={row.id} className="doc-card">
                <header className="doc-card-head row-between">
                  <div>
                    <Link to={`/portal/trips/${row.id}`} className="doc-trip-link">
                      <strong>{row.reference}</strong>
                    </Link>
                    <span className="text-muted" style={{ marginLeft: '0.75rem' }}>
                      {formatDate(row.scheduledDate)}
                    </span>
                  </div>
                  <div className="row" style={{ gap: '0.5rem', alignItems: 'center' }}>
                    <StatusBadge status={row.status} size="sm" audience="client" />
                    <span className="text-muted" style={{ fontSize: '0.8125rem' }}>
                      {podCount
                        ? `${podCount} proof${podCount === 1 ? '' : 's'} of delivery`
                        : 'No proof of delivery yet'}
                    </span>
                  </div>
                </header>
                <ul className="doc-stop-list">
                  {row.documents.length ? (
                    row.documents.map((doc) => (
                      <li key={doc.stopId}>
                        Stop {doc.sequence} · {stopTypeLabel(doc.stopType)}
                        {doc.podId ? (
                          <>
                            {' · '}
                            <strong>{doc.podNumber}</strong>
                            {doc.generatedAt ? ` · ${formatDate(doc.generatedAt)}` : ''}
                            {doc.signed ? ' · Signed' : ' · Awaiting signature'}
                            {' · '}
                            <button
                              type="button"
                              className="link-button"
                              disabled={downloading === doc.podId}
                              onClick={() =>
                                void download(doc.podId!, () => podsApi.downloadPdf(doc.podId!, doc.podNumber))
                              }
                            >
                              {downloading === doc.podId ? 'Preparing…' : 'Download PDF'}
                            </button>
                          </>
                        ) : (
                          <span className="text-muted"> · Not yet available</span>
                        )}
                      </li>
                    ))
                  ) : (
                    <li className="text-muted">No stops recorded yet.</li>
                  )}
                </ul>
                <Link to={`/portal/trips/${row.id}`} className="doc-trip-link" style={{ fontSize: '0.8125rem' }}>
                  View trip →
                </Link>
              </article>
            );
          })}
        </div>
      </div>

      <style>{`
        .portal-nav { margin-bottom: var(--space-lg); }
        .doc-cards { display: flex; flex-direction: column; gap: var(--space-md); }
        .doc-section { margin-bottom: var(--space-xl); }
        .doc-section h2, .doc-section-title { font-size: 1.125rem; color: var(--color-forest); margin-bottom: var(--space-sm); }
        .invoice-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--space-sm); }
        .invoice-row { display: flex; justify-content: space-between; align-items: center; gap: var(--space-md); flex-wrap: wrap; }
        .invoice-actions { display: flex; align-items: center; gap: var(--space-md); }
        .link-button {
          background: none; border: none; padding: 0; color: var(--color-forest);
          text-decoration: underline; cursor: pointer; font: inherit;
        }
        .link-button:disabled { opacity: 0.6; cursor: default; }
        .doc-card {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-md) var(--space-lg);
        }
        .doc-card-head { margin-bottom: var(--space-sm); flex-wrap: wrap; gap: var(--space-sm); }
        .doc-trip-link { color: var(--color-forest); text-decoration: none; }
        .doc-trip-link:hover { text-decoration: underline; }
        .doc-stop-list + .doc-trip-link { display: inline-block; margin-top: var(--space-sm); }
        .doc-stop-list {
          list-style: none;
          margin: 0;
          padding: 0;
          font-size: 0.8125rem;
        }
        .doc-stop-list li + li { margin-top: 0.35rem; }
        .form-error {
          background: #FDE8E8;
          color: var(--color-urgent);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          font-size: 0.875rem;
        }
      `}</style>
    </PortalLayout>
  );
}
